import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, arrayUnion, Timestamp, getDoc, getDocs, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase';
import { useAuth } from '../../../useAuth';
import './pushNotifications.css';

function normalizePhoneForCustomerDocId(phone) {
  if (phone == null || phone === '') return '';
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  if (d.length === 10) return d;
  return '';
}

function getOrderDueDate(order) {
  if (order.dueDate) return order.dueDate;
  if (order.line_items && Array.isArray(order.line_items)) {
    for (const item of order.line_items) {
      if (item.alterationDetails?.dueDate) return item.alterationDetails.dueDate;
    }
  }
  return null;
}

function sortOrdersByDueDate(orders) {
  const toMs = (val) => {
    if (!val) return Number.MAX_SAFE_INTEGER;
    if (val.toDate) return val.toDate().getTime();
    const d = val instanceof Date ? val : new Date(val);
    const t = d.getTime();
    return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
  };
  return [...orders].sort((a, b) => toMs(getOrderDueDate(a)) - toMs(getOrderDueDate(b)));
}

function filterOrdersBySearch(orders, searchTerm) {
  if (!searchTerm.trim()) return orders;
  const searchLower = searchTerm.toLowerCase().trim().replace(/^r:\s*/i, '');
  return orders.filter(
    (order) =>
      order.id.toLowerCase().includes(searchLower) ||
      (order.barcodeId && String(order.barcodeId).toLowerCase().includes(searchLower)) ||
      (order.receiptNumber && order.receiptNumber.toLowerCase().includes(searchLower)) ||
      (order.name && order.name.toLowerCase().includes(searchLower))
  );
}

function isReadyForPickUp(order) {
  return (order?.status || '').toLowerCase().trim() === 'ready for pick up';
}

function normalizeScanValue(scanValue) {
  let trimmed = String(scanValue || '').trim();
  // Scanners often prefix barcode IDs (e.g. "R:1234567890")
  trimmed = trimmed.replace(/^R:\s*/i, '');
  return trimmed.trim();
}

/** Shopify order names are often "#1234"; scanners may omit the hash. */
function orderIdCandidates(trimmed) {
  const raw = String(trimmed || '').trim();
  if (!raw) return [];
  const ids = [raw];
  if (raw.startsWith('#')) ids.push(raw.slice(1));
  else ids.push(`#${raw}`);
  return [...new Set(ids)];
}

function barcodeMatches(barcodeId, trimmed) {
  if (barcodeId == null || barcodeId === '') return false;
  const asString = String(barcodeId);
  if (asString === trimmed) return true;
  if (/^\d+$/.test(trimmed) && asString === String(Number(trimmed))) return true;
  return false;
}

function orderMatchesScan(order, trimmed) {
  if (!order || !trimmed) return false;
  if (barcodeMatches(order.barcodeId, trimmed)) return true;
  const orderId = String(order.id || '');
  if (orderId === trimmed) return true;
  if (orderId.replace(/^#/, '') === trimmed.replace(/^#/, '')) return true;
  if (
    order.receiptNumber &&
    String(order.receiptNumber).trim().toLowerCase() === trimmed.toLowerCase()
  ) {
    return true;
  }
  return false;
}

function findReadyOrderInList(scanValue, orders) {
  const trimmed = normalizeScanValue(scanValue);
  if (!trimmed) return null;
  return orders.find((o) => orderMatchesScan(o, trimmed)) || null;
}

function barcodeIdQueryValues(trimmed) {
  const values = [trimmed];
  // barcodeId may be stored as a number in Firestore
  if (/^\d+$/.test(trimmed)) {
    const asNum = Number(trimmed);
    if (Number.isSafeInteger(asNum)) values.push(asNum);
  }
  return values;
}

async function findOrderDocsByBarcodeId(trimmed) {
  const seen = new Set();
  const docs = [];
  for (const value of barcodeIdQueryValues(trimmed)) {
    const barcodeQuery = query(collection(db, 'Orders'), where('barcodeId', '==', value));
    const barcodeSnap = await getDocs(barcodeQuery);
    barcodeSnap.docs.forEach((d) => {
      if (seen.has(d.id)) return;
      seen.add(d.id);
      docs.push(d);
    });
  }
  return docs;
}

function pickReadyOrRemember(order, state) {
  if (isReadyForPickUp(order)) return { order };
  if (!state.notReady) state.notReady = order;
  return null;
}

async function resolveScannedOrder(scanValue, readyOrders) {
  const trimmed = normalizeScanValue(scanValue);
  if (!trimmed) {
    return { error: 'Empty scan.' };
  }

  const local = findReadyOrderInList(trimmed, readyOrders);
  if (local) {
    return { order: local };
  }

  // Keep looking across barcode / id / receipt. A non-ready hit must not
  // block a ready order that matches under a different key.
  const state = { notReady: null };

  const barcodeDocs = await findOrderDocsByBarcodeId(trimmed);
  for (const d of barcodeDocs) {
    const ready = pickReadyOrRemember({ id: d.id, ...d.data() }, state);
    if (ready) return ready;
  }

  for (const orderId of orderIdCandidates(trimmed)) {
    const byIdSnap = await getDoc(doc(db, 'Orders', orderId));
    if (!byIdSnap.exists()) continue;
    const ready = pickReadyOrRemember({ id: byIdSnap.id, ...byIdSnap.data() }, state);
    if (ready) return ready;
  }

  const receiptQuery = query(collection(db, 'Orders'), where('receiptNumber', '==', trimmed));
  const receiptSnap = await getDocs(receiptQuery);
  for (const d of receiptSnap.docs) {
    const ready = pickReadyOrRemember({ id: d.id, ...d.data() }, state);
    if (ready) return ready;
  }

  if (state.notReady) {
    return {
      error: `Order ${state.notReady.id} is not ready for pick up (current status: ${state.notReady.status || 'unknown'}).`,
    };
  }

  return { error: `No ready-for-pick-up order found for "${trimmed}".` };
}

function PushNotifications() {
  const { user, claims, loading: authLoading } = useAuth();
  const isAdmin = claims.admin === true;

  const [awaitingPickUpOrders, setAwaitingPickUpOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);
  const [markAsPickedUpSending, setMarkAsPickedUpSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showCombinedConfirm, setShowCombinedConfirm] = useState(false);
  const [combinedActionSending, setCombinedActionSending] = useState(false);
  const [customerSmsByPhone, setCustomerSmsByPhone] = useState({});
  const [smsLookupLoading, setSmsLookupLoading] = useState(false);

  const [showScanModal, setShowScanModal] = useState(false);
  const [scanMode, setScanMode] = useState('individual');
  const [scanInputValue, setScanInputValue] = useState('');
  const [scanProcessing, setScanProcessing] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanMessage, setScanMessage] = useState('');
  const [scannedIndividualOrder, setScannedIndividualOrder] = useState(null);
  const [scannedBulkOrders, setScannedBulkOrders] = useState([]);
  const [scanMarkingPickedUp, setScanMarkingPickedUp] = useState(false);
  const [scanSendingNotifications, setScanSendingNotifications] = useState(false);
  const scanInputRef = useRef(null);
  const scanInFlightRef = useRef(false);
  const scanQueueRef = useRef([]);
  const scanRequestIdRef = useRef(0);
  const scanModeRef = useRef(scanMode);
  const awaitingPickUpOrdersRef = useRef(awaitingPickUpOrders);
  const scanBusy = scanMarkingPickedUp || scanSendingNotifications;
  const scanBusyRef = useRef(scanBusy);

  scanModeRef.current = scanMode;
  awaitingPickUpOrdersRef.current = awaitingPickUpOrders;
  scanBusyRef.current = scanBusy;

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) {
      setLoading(false);
      return;
    }

    const ordersRef = collection(db, 'Orders');
    let q;
    try {
      q = query(ordersRef, orderBy('timestamp', 'desc'));
    } catch (err) {
      console.warn('Ordered query failed, using unordered:', err);
      q = query(ordersRef);
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const orders = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const awaiting = orders.filter(
          (order) => (order.status || '').toLowerCase().trim() === 'ready for pick up'
        );
        setAwaitingPickUpOrders(awaiting);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error loading orders:', err);
        setError('Failed to load orders. Please refresh the page.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading, isAdmin]);

  useEffect(() => {
    if (!awaitingPickUpOrders.length) {
      setCustomerSmsByPhone({});
      setSmsLookupLoading(false);
      return;
    }

    const phones = [
      ...new Set(awaitingPickUpOrders.map((o) => normalizePhoneForCustomerDocId(o.phone)).filter(Boolean)),
    ];
    if (phones.length === 0) {
      setCustomerSmsByPhone({});
      setSmsLookupLoading(false);
      return;
    }

    setSmsLookupLoading(true);
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(
        phones.map(async (normalizedPhone) => {
          const [customerSnap, optOutSnap] = await Promise.all([
            getDoc(doc(db, 'Customers', normalizedPhone)),
            getDoc(doc(db, 'SMSOptOuts', normalizedPhone)),
          ]);
          const smsSubscribed = customerSnap.exists() && customerSnap.data()?.smsSubscribed === true;
          const optedOut = optOutSnap.exists() && optOutSnap.data()?.optedOut === true;
          map[normalizedPhone] = { smsSubscribed, optedOut };
        })
      );
      if (!cancelled) {
        setCustomerSmsByPhone(map);
        setSmsLookupLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [awaitingPickUpOrders]);

  const sortedOrders = sortOrdersByDueDate(awaitingPickUpOrders);
  const visibleOrders = filterOrdersBySearch(sortedOrders, searchTerm);
  const combinedNotifyOrders = visibleOrders.filter((o) => selectedIds.has(o.id));
  const combinedPickupOrders = visibleOrders.filter((o) => !selectedIds.has(o.id));
  const busy = sending || markAsPickedUpSending || combinedActionSending;

  const formatOrderLabel = (order) => {
    const parts = [`Order ${order.id}`];
    if (order.receiptNumber) parts.push(`Receipt ${order.receiptNumber}`);
    if (order.name) parts.push(order.name);
    return parts.join(' · ');
  };

  const markOrdersAsPickedUpByIds = async (orderIds) => {
    if (!orderIds.length) return 0;
    const statusLogEntry = {
      userId: user?.uid || 'unknown',
      userName: user?.displayName || user?.email || 'Unknown User',
      status: 'picked up',
      timestamp: Timestamp.now(),
    };
    for (const orderId of orderIds) {
      await updateDoc(doc(db, 'Orders', orderId), {
        status: 'picked up',
        statusLog: arrayUnion(statusLogEntry),
      });
    }
    return orderIds.length;
  };

  const resetScanState = useCallback(() => {
    scanRequestIdRef.current += 1;
    scanInFlightRef.current = false;
    scanQueueRef.current = [];
    setScanInputValue('');
    setScanError('');
    setScanMessage('');
    setScannedIndividualOrder(null);
    setScannedBulkOrders([]);
    setScanProcessing(false);
    setScanMarkingPickedUp(false);
    setScanSendingNotifications(false);
  }, []);

  const openScanModal = () => {
    resetScanState();
    setScanMode('individual');
    setShowScanModal(true);
  };

  const closeScanModal = () => {
    setShowScanModal(false);
    resetScanState();
  };

  useEffect(() => {
    if (!showScanModal) return;
    const timer = setTimeout(() => scanInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [showScanModal, scanMode]);

  const processScan = useCallback(async (rawValue) => {
    const value = normalizeScanValue(rawValue);
    if (!value || scanBusyRef.current) return;

    // Keep the input free for the next barcode while a lookup is in flight.
    if (scanInFlightRef.current) {
      scanQueueRef.current.push(value);
      setScanInputValue('');
      return;
    }

    scanInFlightRef.current = true;
    const requestId = ++scanRequestIdRef.current;
    setScanProcessing(true);
    setScanInputValue('');
    setScanError('');
    setScanMessage('');

    try {
      const { order, error } = await resolveScannedOrder(value, awaitingPickUpOrdersRef.current);

      // Ignore stale results after close/reset or a superseded request.
      if (requestId !== scanRequestIdRef.current) return;

      if (error) {
        setScanError(error);
        return;
      }

      const barcodeLabel = order.barcodeId ? ` · Barcode ${order.barcodeId}` : '';
      if (scanModeRef.current === 'individual') {
        setScannedIndividualOrder(order);
        setScanMessage(`Scanned order ${order.id}${barcodeLabel}.`);
      } else {
        setScannedBulkOrders((prev) => {
          if (prev.some((o) => o.id === order.id)) {
            setScanMessage(`Order ${order.id} is already in the bulk list.`);
            return prev;
          }
          setScanMessage(`Added order ${order.id}${barcodeLabel} (${prev.length + 1} total).`);
          return [...prev, order];
        });
      }
    } catch (err) {
      if (requestId !== scanRequestIdRef.current) return;
      setScanError(err.message || 'Scan failed.');
    } finally {
      if (requestId === scanRequestIdRef.current) {
        scanInFlightRef.current = false;
        setScanProcessing(false);
        setTimeout(() => {
          scanInputRef.current?.focus();
          const next = scanQueueRef.current.shift();
          if (next) processScan(next);
        }, 0);
      }
    }
  }, []);

  const handleScanSubmit = (e) => {
    e.preventDefault();
    const value = scanInputValue;
    setScanInputValue('');
    processScan(value);
  };

  const markScannedIndividualPickedUp = async () => {
    if (!scannedIndividualOrder) return;
    setScanMarkingPickedUp(true);
    setScanError('');
    try {
      const count = await markOrdersAsPickedUpByIds([scannedIndividualOrder.id]);
      setResult({ sent: 0, failed: 0, markedAsPickedUp: count });
      setScanMessage(`Order ${scannedIndividualOrder.id} marked as picked up.`);
      setScannedIndividualOrder(null);
    } catch (err) {
      setScanError(err.message || 'Failed to mark order as picked up.');
    } finally {
      setScanMarkingPickedUp(false);
      setTimeout(() => scanInputRef.current?.focus(), 0);
    }
  };

  const markScannedBulkPickedUp = async () => {
    if (scannedBulkOrders.length === 0) return;
    setScanMarkingPickedUp(true);
    setScanError('');
    try {
      const ids = scannedBulkOrders.map((o) => o.id);
      const count = await markOrdersAsPickedUpByIds(ids);
      setResult({ sent: 0, failed: 0, markedAsPickedUp: count });
      setScanMessage(`${count} order${count !== 1 ? 's' : ''} marked as picked up.`);
      setScannedBulkOrders([]);
    } catch (err) {
      setScanError(err.message || 'Failed to mark orders as picked up.');
    } finally {
      setScanMarkingPickedUp(false);
      setTimeout(() => scanInputRef.current?.focus(), 0);
    }
  };

  const sendScannedBulkNotifications = async () => {
    if (scannedBulkOrders.length === 0) return;
    setScanSendingNotifications(true);
    setScanError('');
    try {
      const resend = httpsCallable(functions, 'resendReadyForPickUpNotification2');
      const { data } = await resend({ orderIds: scannedBulkOrders.map((o) => o.id) });
      setResult(data);
      setScanMessage(
        `Notifications sent: ${data?.sent || 0} succeeded, ${data?.failed || 0} failed.`
      );
      if ((data?.sent || 0) > 0 && (data?.failed || 0) === 0) {
        setScannedBulkOrders([]);
      }
    } catch (err) {
      setScanError(err.message || 'Failed to send push notifications.');
    } finally {
      setScanSendingNotifications(false);
      setTimeout(() => scanInputRef.current?.focus(), 0);
    }
  };

  const removeBulkScannedOrder = (orderId) => {
    setScannedBulkOrders((prev) => prev.filter((o) => o.id !== orderId));
  };

  const markSelectedOrdersAsPickedUp = async () => {
    if (selectedIds.size === 0) return;
    setMarkAsPickedUpSending(true);
    setResult(null);
    try {
      const count = await markOrdersAsPickedUpByIds(Array.from(selectedIds));
      setResult({ sent: 0, failed: 0, markedAsPickedUp: count });
      setSelectedIds(new Set());
    } catch (err) {
      setResult({
        sent: 0,
        failed: selectedIds.size,
        errors: [err.message || String(err)],
        markedAsPickedUp: false,
      });
    } finally {
      setMarkAsPickedUpSending(false);
    }
  };

  const runCombinedPushAndPickup = async (notifyIds, pickupIds) => {
    setCombinedActionSending(true);
    setResult(null);
    let sent = 0;
    let failed = 0;
    let errors = [];
    let markedAsPickedUp = 0;
    try {
      if (notifyIds.length > 0) {
        const resend = httpsCallable(functions, 'resendReadyForPickUpNotification2');
        const { data } = await resend({ orderIds: notifyIds });
        sent = data?.sent || 0;
        failed = data?.failed || 0;
        errors = Array.isArray(data?.errors) ? data.errors : [];
      }
      if (pickupIds.length > 0) {
        markedAsPickedUp = await markOrdersAsPickedUpByIds(pickupIds);
      }
      setResult({ sent, failed, errors, markedAsPickedUp });
      setSelectedIds(new Set());
      setShowCombinedConfirm(false);
    } catch (err) {
      setResult({
        sent,
        failed: failed || notifyIds.length,
        errors: [...errors, err.message || String(err)],
        markedAsPickedUp,
      });
    } finally {
      setCombinedActionSending(false);
    }
  };

  const handleResendNotifications = async () => {
    if (selectedIds.size === 0) return;
    setSending(true);
    setResult(null);
    try {
      const resend = httpsCallable(functions, 'resendReadyForPickUpNotification2');
      const { data } = await resend({ orderIds: Array.from(selectedIds) });
      setResult(data);
      if (data.sent > 0 && data.failed === 0) setSelectedIds(new Set());
    } catch (err) {
      setResult({ sent: 0, failed: selectedIds.size, errors: [err.message || String(err)] });
    } finally {
      setSending(false);
    }
  };

  if (authLoading) {
    return (
      <div className="push-notifications-container">
        <p className="push-notifications-loading">Loading…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="push-notifications-container">
        <p className="push-notifications-error">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="push-notifications-container">
      <div className="push-notifications-layout">
        <div className="push-notifications-card">
          {loading ? (
            <p className="push-notifications-loading">Loading orders awaiting pick up…</p>
          ) : error ? (
            <p className="push-notifications-error">{error}</p>
          ) : (
            <>
              <div className="push-notification-search-actions-row">
                <div className="push-notification-search-row">
                  <label htmlFor="push-notifications-search" className="push-notification-search-label">
                    Search:
                  </label>
                  <input
                    id="push-notifications-search"
                    type="text"
                    className="push-notification-search-input"
                    placeholder="Search by order ID, receipt # or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="push-notification-search-clear"
                      onClick={() => setSearchTerm('')}
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="push-notification-actions">
                  <button
                    type="button"
                    className="push-notification-select-btn push-notification-scan-btn"
                    onClick={openScanModal}
                  >
                    Scan
                  </button>
                  <button
                    type="button"
                    className="push-notification-select-btn"
                    onClick={() => setSelectedIds(new Set(visibleOrders.map((o) => o.id)))}
                    disabled={visibleOrders.length === 0}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="push-notification-select-btn"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={selectedIds.size === 0}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {searchTerm.trim() && (
                <p className="push-notification-search-count">
                  Showing {visibleOrders.length} of {sortedOrders.length} orders
                </p>
              )}

              {!searchTerm.trim() && (
                <p className="push-notification-order-count">
                  {sortedOrders.length} order{sortedOrders.length !== 1 ? 's' : ''} ready for pick up
                </p>
              )}

              {visibleOrders.length === 0 ? (
                <div className="push-notifications-empty">
                  <h3>{searchTerm.trim() ? 'No orders match your search' : 'No orders awaiting pick up'}</h3>
                  <p>
                    {searchTerm.trim()
                      ? 'Try a different order ID, receipt number, or name.'
                      : 'No orders are currently ready for pick up.'}
                  </p>
                </div>
              ) : (
                <div className="push-notification-list push-notification-list-page">
                  {visibleOrders.map((order) => {
                    const phoneKey = normalizePhoneForCustomerDocId(order.phone);
                    const customerSms = phoneKey ? customerSmsByPhone[phoneKey] : undefined;
                    let optInLabel = 'Not opted in';
                    let optInClass = 'not-opted-in';
                    if (!phoneKey) {
                      optInLabel = 'No phone on order';
                    } else if (smsLookupLoading && customerSms === undefined) {
                      optInLabel = 'Checking subscription…';
                      optInClass = 'checking';
                    } else if (customerSms?.smsSubscribed === true && !customerSms.optedOut) {
                      optInLabel = 'Opted in';
                      optInClass = 'opted-in';
                    }
                    return (
                      <label key={order.id} className="push-notification-item">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(order.id)}
                          onChange={(e) => {
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(order.id);
                              else next.delete(order.id);
                              return next;
                            });
                          }}
                        />
                        <span className="push-notification-item-label">Order: {order.id}</span>
                        {order.receiptNumber && (
                          <span className="push-notification-item-receipt">Receipt: {order.receiptNumber}</span>
                        )}
                        {order.name && <span className="push-notification-item-name">{order.name}</span>}
                        <span className={`push-notification-item-optin ${optInClass}`}>{optInLabel}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {result && (
                <div className={`push-notification-result ${result.failed > 0 ? 'has-errors' : 'success'}`}>
                  {(result.sent > 0 || result.failed > 0) && (
                    <p>
                      Notifications — Sent: {result.sent || 0}, Failed: {result.failed || 0}
                    </p>
                  )}
                  {result.markedAsPickedUp ? (
                    <p>
                      {result.markedAsPickedUp} order{result.markedAsPickedUp !== 1 ? 's' : ''} marked as picked up.
                    </p>
                  ) : null}
                  {result.errors?.length > 0 && (
                    <ul className="push-notification-errors">
                      {result.errors.slice(0, 10).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {result.errors.length > 10 && <li>… and {result.errors.length - 10} more</li>}
                    </ul>
                  )}
                </div>
              )}

              <div className="push-notifications-footer">
                <button
                  type="button"
                  className="push-notifications-btn mark-picked-up-btn"
                  disabled={selectedIds.size === 0 || busy}
                  onClick={markSelectedOrdersAsPickedUp}
                >
                  {markAsPickedUpSending ? 'Marking...' : `Mark as picked up (${selectedIds.size})`}
                </button>
                <button
                  type="button"
                  className="push-notifications-btn save-btn"
                  disabled={selectedIds.size === 0 || busy}
                  onClick={handleResendNotifications}
                >
                  {sending ? 'Sending...' : `Resend notification (${selectedIds.size})`}
                </button>
                <button
                  type="button"
                  className="push-notifications-btn combined-action-btn"
                  disabled={combinedPickupOrders.length === 0 || busy}
                  onClick={() => {
                    setResult(null);
                    setShowCombinedConfirm(true);
                  }}
                >
                  {combinedActionSending ? 'Working...' : 'Send & Complete'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showScanModal && (
        <div className="push-notification-confirm-overlay" onClick={closeScanModal}>
          <div
            className="push-notification-scan-card"
            role="dialog"
            aria-labelledby="push-scan-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="push-notification-scan-header">
              <h3 id="push-scan-title">Barcode scan pickup</h3>
              <button type="button" className="push-notification-scan-close" onClick={closeScanModal} aria-label="Close">
                ×
              </button>
            </div>
            <p className="push-notification-scan-intro">
              Scan the order barcode ID. The scanner sends Enter after each scan — keep this window open and scan.
            </p>

            <div className="push-notification-scan-mode-toggle" role="tablist" aria-label="Scan mode">
              <button
                type="button"
                role="tab"
                aria-selected={scanMode === 'individual'}
                className={scanMode === 'individual' ? 'active' : ''}
                onClick={() => {
                  setScanMode('individual');
                  scanQueueRef.current = [];
                  setScanError('');
                  setScanMessage('');
                  setScannedBulkOrders([]);
                }}
              >
                Individual
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={scanMode === 'bulk'}
                className={scanMode === 'bulk' ? 'active' : ''}
                onClick={() => {
                  setScanMode('bulk');
                  scanQueueRef.current = [];
                  setScanError('');
                  setScanMessage('');
                  setScannedIndividualOrder(null);
                }}
              >
                Bulk
              </button>
            </div>

            <form className="push-notification-scan-form" onSubmit={handleScanSubmit}>
              <label htmlFor="push-scan-input" className="push-notification-scan-label">
                Scanner input
              </label>
              <input
                ref={scanInputRef}
                id="push-scan-input"
                type="text"
                className="push-notification-scan-input"
                value={scanInputValue}
                onChange={(e) => setScanInputValue(e.target.value)}
                placeholder={scanProcessing ? 'Processing… (next scan will queue)' : 'Click here and scan barcode…'}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                disabled={scanBusy}
              />
              <button
                type="submit"
                className="push-notifications-btn save-btn push-notification-scan-submit"
                disabled={!scanInputValue.trim() || scanBusy}
              >
                {scanProcessing ? 'Looking up…' : 'Look up scan'}
              </button>
            </form>

            {scanMessage && <p className="push-notification-scan-message">{scanMessage}</p>}
            {scanError && <p className="push-notification-scan-error">{scanError}</p>}

            {scanMode === 'individual' && scannedIndividualOrder && (
              <div className="push-notification-scan-order-card">
                <div className="push-notification-scan-order-meta">
                  <strong>Order {scannedIndividualOrder.id}</strong>
                  {scannedIndividualOrder.barcodeId && (
                    <span>Barcode ID {scannedIndividualOrder.barcodeId}</span>
                  )}
                  {scannedIndividualOrder.receiptNumber && (
                    <span>Receipt {scannedIndividualOrder.receiptNumber}</span>
                  )}
                  {scannedIndividualOrder.name && <span>{scannedIndividualOrder.name}</span>}
                </div>
                <button
                  type="button"
                  className="push-notifications-btn mark-picked-up-btn"
                  disabled={scanBusy}
                  onClick={markScannedIndividualPickedUp}
                >
                  {scanMarkingPickedUp ? 'Marking…' : 'Mark as picked up'}
                </button>
              </div>
            )}

            {scanMode === 'bulk' && (
              <div className="push-notification-scan-bulk">
                <div className="push-notification-scan-bulk-header">
                  <h4>Scanned orders ({scannedBulkOrders.length})</h4>
                  {scannedBulkOrders.length > 0 && (
                    <button
                      type="button"
                      className="push-notification-select-btn"
                      onClick={() => setScannedBulkOrders([])}
                      disabled={scanBusy}
                    >
                      Clear list
                    </button>
                  )}
                </div>
                {scannedBulkOrders.length === 0 ? (
                  <p className="push-notification-scan-bulk-empty">
                    Scan barcodes to add orders, then mark them as picked up or send push notifications.
                  </p>
                ) : (
                  <ul className="push-notification-scan-bulk-list">
                    {scannedBulkOrders.map((order) => (
                      <li key={order.id}>
                        <span>
                          Order {order.id}
                          {order.barcodeId ? ` · Barcode ${order.barcodeId}` : ''}
                          {order.receiptNumber ? ` · Receipt ${order.receiptNumber}` : ''}
                          {order.name ? ` · ${order.name}` : ''}
                        </span>
                        <button
                          type="button"
                          className="push-notification-scan-remove"
                          onClick={() => removeBulkScannedOrder(order.id)}
                          disabled={scanBusy}
                          aria-label={`Remove order ${order.id} from list`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="push-notification-scan-bulk-actions">
                  <button
                    type="button"
                    className="push-notifications-btn mark-picked-up-btn"
                    disabled={scannedBulkOrders.length === 0 || scanBusy}
                    onClick={markScannedBulkPickedUp}
                  >
                    {scanMarkingPickedUp
                      ? 'Marking…'
                      : `Mark ${scannedBulkOrders.length} as picked up`}
                  </button>
                  <button
                    type="button"
                    className="push-notifications-btn save-btn"
                    disabled={scannedBulkOrders.length === 0 || scanBusy}
                    onClick={sendScannedBulkNotifications}
                  >
                    {scanSendingNotifications
                      ? 'Sending…'
                      : `Send push notifications (${scannedBulkOrders.length})`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showCombinedConfirm && (
        <div className="push-notification-confirm-overlay">
          <div className="push-notification-confirm-card" role="dialog" aria-labelledby="push-combined-confirm-title">
            <h3 id="push-combined-confirm-title">Confirm combined action</h3>
            <p className="push-notification-confirm-intro">Please review before continuing:</p>
            <div className="push-notification-confirm-section notify">
              <h4>
                Sending notifications to {combinedNotifyOrders.length} order
                {combinedNotifyOrders.length !== 1 ? 's' : ''}
              </h4>
              {combinedNotifyOrders.length === 0 ? (
                <p className="push-notification-confirm-empty">No orders selected — no notifications will be sent.</p>
              ) : (
                <ul>
                  {combinedNotifyOrders.map((order) => (
                    <li key={order.id}>{formatOrderLabel(order)}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="push-notification-confirm-section pickup">
              <h4>
                Moving {combinedPickupOrders.length} order{combinedPickupOrders.length !== 1 ? 's' : ''} to picked up
              </h4>
              {combinedPickupOrders.length === 0 ? (
                <p className="push-notification-confirm-empty">No unselected orders — nothing will be marked picked up.</p>
              ) : (
                <ul>
                  {combinedPickupOrders.map((order) => (
                    <li key={order.id}>{formatOrderLabel(order)}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="push-notification-confirm-actions">
              <button
                type="button"
                className="push-notifications-btn"
                disabled={combinedActionSending}
                onClick={() => setShowCombinedConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="push-notifications-btn combined-action-btn"
                disabled={
                  combinedActionSending || (combinedNotifyOrders.length === 0 && combinedPickupOrders.length === 0)
                }
                onClick={() =>
                  runCombinedPushAndPickup(
                    combinedNotifyOrders.map((o) => o.id),
                    combinedPickupOrders.map((o) => o.id)
                  )
                }
              >
                {combinedActionSending ? 'Working...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PushNotifications;
